/**
 * ProductForm Component
 * Form for adding new products during receive stock
 */

import type { Category } from '@/types/index.ts';
import { requiresItemsPerUnit } from '@/types/index.ts';

interface ProductFormProps {
  productName: string;
  onProductNameChange: (value: string) => void;
  categoryId: number | '';
  onCategoryIdChange: (value: number | '') => void;
  unit: string;
  onUnitChange: (value: string) => void;
  unitPrice: string;
  onUnitPriceChange: (value: string) => void;
  costPrice: string;
  onCostPriceChange: (value: string) => void;
  markupType: '$' | '%';
  onMarkupTypeChange: (value: '$' | '%') => void;
  markupValue: string;
  onMarkupValueChange: (value: string) => void;
  minQuantity: string;
  onMinQuantityChange: (value: string) => void;
  quantity: number | '';
  onQuantityChange: (value: number | '') => void;
  itemsPerUnit: number | '';
  onItemsPerUnitChange: (value: number | '') => void;
  categories: Category[];
  units: string[];
  loadingData: boolean;
}

export default function ProductForm({
  productName,
  onProductNameChange,
  categoryId,
  onCategoryIdChange,
  unit,
  onUnitChange,
  unitPrice,
  onUnitPriceChange,
  costPrice,
  onCostPriceChange,
  markupType,
  onMarkupTypeChange,
  markupValue,
  onMarkupValueChange,
  minQuantity,
  onMinQuantityChange,
  quantity,
  onQuantityChange,
  itemsPerUnit,
  onItemsPerUnitChange,
  categories,
  units,
  loadingData,
}: ProductFormProps) {
  const needsItemsPerUnit = requiresItemsPerUnit(unit);

  const handleQuantityChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      onQuantityChange(value === '' ? '' : Number(value));
    }
  };

  const handleItemsPerUnitChange = (value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      onItemsPerUnitChange(value === '' ? '' : Number(value));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      [46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }
    if (
      (e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const handleCostPriceChange = (value: string) => {
    onCostPriceChange(value);
    // Recalculate selling price from cost + markup
    const cost = parseFloat(value);
    const markup = parseFloat(markupValue);
    if (!isNaN(cost) && cost > 0 && !isNaN(markup) && markup >= 0) {
      const selling = markupType === '%'
        ? cost * (1 + markup / 100)
        : cost + markup;
      onUnitPriceChange(selling.toFixed(2));
    }
  };

  const handleMarkupValueChange = (value: string) => {
    onMarkupValueChange(value);
    const cost = parseFloat(costPrice);
    const markup = parseFloat(value);
    if (!isNaN(cost) && cost > 0 && !isNaN(markup) && markup >= 0) {
      const selling = markupType === '%'
        ? cost * (1 + markup / 100)
        : cost + markup;
      onUnitPriceChange(selling.toFixed(2));
    }
  };

  const handleMarkupTypeChange = (type: '$' | '%') => {
    onMarkupTypeChange(type);
    const cost = parseFloat(costPrice);
    const markup = parseFloat(markupValue);
    if (!isNaN(cost) && cost > 0 && !isNaN(markup) && markup >= 0) {
      const selling = type === '%'
        ? cost * (1 + markup / 100)
        : cost + markup;
      onUnitPriceChange(selling.toFixed(2));
    }
  };

  const totalCost = (() => {
    const cost = parseFloat(costPrice);
    const qty = typeof quantity === 'number' ? quantity : Number(quantity);
    const ipu = typeof itemsPerUnit === 'number' ? itemsPerUnit : Number(itemsPerUnit);
    const finalQty = needsItemsPerUnit && itemsPerUnit !== '' ? qty * ipu : qty;
    if (!isNaN(cost) && cost > 0 && finalQty > 0) {
      return (cost * finalQty).toFixed(2);
    }
    return null;
  })();

  return (
    <div className="mb-4 space-y-3">
      {/* Product Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Product Name <span className="text-[#ed1438]">*</span>
        </label>
        <input
          type="text"
          value={productName}
          onChange={(e) => onProductNameChange(e.target.value)}
          placeholder="Enter product name..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category <span className="text-[#ed1438]">*</span>
        </label>
        <select
          value={categoryId}
          onChange={(e) => onCategoryIdChange(e.target.value ? Number(e.target.value) : '')}
          disabled={loadingData}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed"
        >
          <option value="">{loadingData ? 'Loading categories...' : 'Select category...'}</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Unit */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Unit of Measure <span className="text-[#ed1438]">*</span>
        </label>
        <select
          value={unit}
          onChange={(e) => {
            onUnitChange(e.target.value);
            if (!requiresItemsPerUnit(e.target.value)) {
              onItemsPerUnitChange('');
            }
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1] cursor-pointer"
        >
          <option value="">Select unit...</option>
          {units.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </div>

      {/* Quantity Fields */}
      {needsItemsPerUnit ? (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of {unit} <span className="text-[#ed1438]">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={quantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              onKeyDown={handleKeyDown}
              placeholder={`Enter number of ${unit}`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Items per {unit} <span className="text-[#ed1438]">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={itemsPerUnit}
              onChange={(e) => handleItemsPerUnitChange(e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              onKeyDown={handleKeyDown}
              placeholder={`How many items in each ${unit}?`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
            />
            <p className="text-xs text-gray-500 mt-1">
              Total quantity will be:{' '}
              {quantity !== '' && itemsPerUnit !== ''
                ? `${quantity} × ${itemsPerUnit} = ${
                    (typeof quantity === 'number' ? quantity : Number(quantity)) *
                    (typeof itemsPerUnit === 'number' ? itemsPerUnit : Number(itemsPerUnit))
                  }`
                : 'Quantity × Items per unit'}
            </p>
          </div>
        </>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity <span className="text-[#ed1438]">*</span>
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={quantity}
            onChange={(e) => handleQuantityChange(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            onKeyDown={handleKeyDown}
            placeholder="Enter quantity"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
          />
        </div>
      )}

      {/* Pricing Section */}
      <div className="pt-1 border-t border-gray-100 space-y-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pricing</p>

        {/* Unit Cost */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Unit Cost (JMD) <span className="text-[#ed1438]">*</span>
          </label>
          <input
            type="text"
            inputMode="decimal"
            value={costPrice}
            onChange={(e) => handleCostPriceChange(e.target.value)}
            placeholder="e.g. 50.00"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
          />
          {totalCost && (
            <p className="text-xs text-gray-500 mt-1">
              Total purchase cost: <span className="font-semibold text-gray-700">JMD {Number(totalCost).toLocaleString('en-JM', { minimumFractionDigits: 2 })}</span>
            </p>
          )}
        </div>

        {/* Markup */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Markup <span className="text-[#ed1438]">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex rounded-lg overflow-hidden border border-gray-300">
              {(['$', '%'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => handleMarkupTypeChange(t)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    markupType === t
                      ? 'bg-[#0997a4] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={markupValue}
              onChange={(e) => handleMarkupValueChange(e.target.value)}
              placeholder={markupType === '%' ? 'e.g. 40' : 'e.g. 20.00'}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
            />
          </div>
        </div>

        {/* Selling Price — read-only */}
        <p className="text-sm text-gray-600">
          Selling Price:{' '}
          {unitPrice && parseFloat(unitPrice) > 0
            ? <span className="font-semibold text-gray-900">JMD {parseFloat(unitPrice).toLocaleString('en-JM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            : <span className="text-gray-400">calculated from cost &amp; markup</span>
          }
        </p>
      </div>

      {/* Minimum Stock Quantity (Optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Minimum Stock Quantity <span className="text-xs font-normal text-gray-500">(optional)</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={minQuantity}
          onChange={(e) => onMinQuantityChange(e.target.value)}
          placeholder="Enter minimum stock..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#50b3e1]"
        />
      </div>
    </div>
  );
}